// components/insights/competitive/CompetitiveAnalysisView.jsx
// Main competitive analysis view component - refactored into modular components
// Coordinates tab navigation and delegates rendering to specialized tab components

import React, { useState } from 'react';
import { Download, RefreshCw, AlertTriangle } from 'lucide-react';
import CustomTooltip from '../../common/Tooltip';

// Import shared components
import TabButton from './shared/TabButton';
import GameModalWrapper from './shared/GameModalWrapper';

// Import tab components
import OverviewTab from './tabs/OverviewTab';
import MarketTab from './tabs/MarketTab';
import CompetitorsTab from './tabs/CompetitorsTab';
import RecommendationsTab from './tabs/RecommendationsTab';

/**
 * Main competitive analysis view component
 * Acts as a container and coordinator for competitive analysis tabs
 * @param {Object} props - Component props
 * @param {Object} props.analysis - Complete competitive analysis data
 * @param {Function} props.onNewAnalysis - Function to start a new analysis
 * @param {Object} props.summary - Analysis summary data
 * @returns {JSX.Element} Competitive analysis view component
 */
const CompetitiveAnalysisView = ({ analysis, onNewAnalysis, summary }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedGame, setSelectedGame] = useState(null);
    const [detailsCache, setDetailsCache] = useState({});

    // Add defensive check for missing analysis
    if (!analysis) {
        return (
            <div className="bg-white rounded-lg shadow p-6 text-center">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Analysis Unavailable</h3>
                <p className="text-gray-600 mb-4">
                    Detailed competitive analysis could not be loaded.
                </p>
                {onNewAnalysis && (
                    <button
                        onClick={onNewAnalysis}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        <RefreshCw className="w-4 h-4" />
                        New Analysis
                    </button>
                )}
            </div>
        );
    }

    // Safely extract necessary properties with defaults
    const { summary: analysisSummary = {}, market = {}, competitors = {}, pricing = {}, geography = {} } = analysis;
    const metadata = analysis.metadata || { analyzedAt: new Date().toISOString() };

    // Export functionality
    const handleExport = () => {
        const exportData = {
            analysis,
            summary,
            exportedAt: new Date().toISOString()
        };
        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `market-analysis-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    /**
     * Render the appropriate tab content based on active tab
     * @returns {JSX.Element} Tab content component
     */
    const renderContent = () => {
        switch (activeTab) {
            case 'overview':
                return <OverviewTab analysis={analysis} />;
            case 'market':
                return <MarketTab analysis={analysis} />;
            case 'competitors':
                return <CompetitorsTab analysis={analysis} onGameClick={(game) => setSelectedGame(game)} />;
            case 'recommendations':
                return <RecommendationsTab analysis={analysis} />;
            default:
                return <OverviewTab analysis={analysis} />;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header with title and action buttons */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Market Analysis</h2>
                    {summary && (
                        <p className="text-sm text-gray-600 mt-1">
                            Based on {summary.analyzedGames || 0} analyzed games
                            {summary.errorCount > 0 && ` (${summary.errorCount} analysis errors)`}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {metadata.analyzedAt && (
                        <span className="text-sm text-gray-500">
                            Updated {new Date(metadata.analyzedAt).toLocaleDateString()}
                        </span>
                    )}
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Export
                    </button>
                    {onNewAnalysis && (
                        <button
                            onClick={onNewAnalysis}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            New Analysis
                        </button>
                    )}
                </div>
            </div>

            {/* Main content area */}
            <div className="bg-white rounded-lg shadow">
                {/* Tab Navigation */}
                <div className="border-b px-6 py-4">
                    <div className="flex space-x-1">
                        <TabButton
                            active={activeTab === 'overview'}
                            onClick={() => setActiveTab('overview')}
                        >
                            Overview
                        </TabButton>
                        <TabButton
                            active={activeTab === 'market'}
                            onClick={() => setActiveTab('market')}
                        >
                            Market Analysis
                        </TabButton>
                        <TabButton
                            active={activeTab === 'competitors'}
                            onClick={() => setActiveTab('competitors')}
                        >
                            Competitors
                        </TabButton>
                        <TabButton
                            active={activeTab === 'recommendations'}
                            onClick={() => setActiveTab('recommendations')}
                        >
                            Recommendations
                        </TabButton>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="min-h-[600px] max-h-[70vh] overflow-auto px-6 py-4">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default CompetitiveAnalysisView;
